// SccClickwrap — Phase 5 Plan 05-04 Task 3 (D-88).
// Phase 12 Plan 12-10: shell migrated to @telegram-apps/telegram-ui.
//
// **ISSUE-07 fix:** SCC text rendered INLINE as scrollable markdown (via
// react-markdown). PDF fallback opens in external browser via
// window.Telegram.WebApp.openLink — NEVER embedded.
//
// IMPORTANT (UI-SPEC §10 out-of-scope, T-12-10-02): clickwrap POST /
// content_hash logic is NOT touched — only the shell (typography, scrollable
// Card, buttons) is migrated. Scroll-gate ref/onScroll/data-testid preserved.
//
// Flow (post-registration for country IN ('TR','RS')):
//   1. useQuery fetches GET /api/v1/legal/scc?locale={X}&version=1.0&format=md
//   2. Render markdown body inside a scrollable container (TOUCH_TARGET_MIN typography).
//   3. onScroll handler: enable acceptButton ONLY когда scrolled-to-bottom (basic readability gate).
//   4. Checkbox "I have read and accept" + accept button (disabled until both checked + scrolled).
//   5. Compute SHA-256 of markdown client-side via legalApi.sha256OfText (defence-in-depth).
//   6. POST /api/v1/companies/scc-accept → on 201 → navigate to /trial-activated.
//   7. PDF fallback button → window.Telegram.WebApp.openLink(getPdfUrl(...)) — external browser.

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Caption, Card, Cell, Title } from "@telegram-apps/telegram-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  type LegalDocMarkdownResponse,
  type LegalLocale,
  type SccAcceptResponse,
  legalApi,
} from "../api/legal";
import { useAuth } from "../auth/useAuth";
import { renderLegalMarkdown } from "../lib/legalRedacted";
import { SPACING, TOUCH_TARGET_MIN, TYPOGRAPHY } from "../lib/tokens";

interface TelegramWebAppLike {
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebAppLike };
  }
}

interface SccClickwrapProps {
  baseUrl: string;
  /** Override company_id (test injection). Default reads JWT.cid via useAuth. */
  companyId?: number;
}

const DEFAULT_VERSION = "1.0";

const HINT_COLOR = "var(--tgui--hint_color, var(--tg-theme-hint-color, #666))";
const DESTRUCTIVE_COLOR =
  "var(--tgui--destructive_text_color, var(--tg-theme-destructive-text-color, red))";

function pickSccLocale(uiLocale: string): LegalLocale {
  // SCC is published в EN + TR only (per D-88). Map UI locale → SCC locale.
  if (uiLocale === "tr") return "tr";
  return "en";
}

export function SccClickwrap({ baseUrl, companyId }: SccClickwrapProps): React.JSX.Element {
  const { t, i18n } = useTranslation("settings_legal");
  const navigate = useNavigate();
  const { apiClient, jwt } = useAuth();
  const [searchParams] = useSearchParams();

  const sccLocale = useMemo<LegalLocale>(
    () => pickSccLocale(searchParams.get("locale") || i18n.language),
    [searchParams, i18n.language],
  );

  // Decode company_id from JWT.cid if not passed explicitly
  const effectiveCompanyId = useMemo<number | null>(() => {
    if (companyId !== undefined) return companyId;
    if (!jwt) return null;
    try {
      const parts = jwt.split(".");
      const payloadPart = parts[1];
      if (!payloadPart) return null;
      const b64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
      const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as { cid?: number };
      return typeof payload.cid === "number" ? payload.cid : null;
    } catch {
      return null;
    }
  }, [companyId, jwt]);

  // Load SCC markdown source (inline rendering — NOT embedded PDF per ISSUE-07)
  const sccQuery = useQuery<LegalDocMarkdownResponse>({
    queryKey: ["legal", "scc", sccLocale, DEFAULT_VERSION],
    queryFn: () => legalApi.getDocMarkdown(baseUrl, "scc", sccLocale, DEFAULT_VERSION),
  });

  const [agreed, setAgreed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reset gate state when SCC text changes (e.g. locale switch)
  useEffect(() => {
    setAgreed(false);
    setScrolledToBottom(false);
  }, [sccQuery.data?.sha256]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const t_ = e.currentTarget;
    if (t_.scrollHeight - t_.scrollTop <= t_.clientHeight + 50) {
      setScrolledToBottom(true);
    }
  };

  const acceptMutation = useMutation<SccAcceptResponse, Error, void>({
    mutationFn: async () => {
      if (!sccQuery.data) throw new Error("SCC text not loaded yet");
      if (effectiveCompanyId === null) throw new Error("company_id not available");
      // Defence-in-depth: re-compute hash client-side. Server still validates.
      const clientHash = await legalApi.sha256OfText(sccQuery.data.markdown);
      if (clientHash !== sccQuery.data.sha256) {
        // Should never happen — same text returned from server. If it does,
        // means the bytes changed between read + hash → bail.
        throw new Error("client/server hash mismatch on SCC text");
      }
      return legalApi.acceptScc(apiClient, {
        company_id: effectiveCompanyId,
        scc_version: sccQuery.data.version,
        scc_hash: sccQuery.data.sha256,
        locale: sccQuery.data.locale,
        signature_method: "clickwrap",
      });
    },
    onSuccess: () => {
      navigate("/trial");
    },
  });

  const openPdfFallback = (): void => {
    const pdfUrl = legalApi.getPdfUrl(baseUrl, "scc", sccLocale, DEFAULT_VERSION);
    // ISSUE-07 enforcement — open in external browser, NOT in WebView.
    const tg = window.Telegram?.WebApp;
    if (tg?.openLink) {
      tg.openLink(pdfUrl);
    } else {
      // Fallback for dev (no Telegram WebApp): just open in new tab.
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  const acceptEnabled = agreed && scrolledToBottom && !acceptMutation.isPending;

  return (
    <div style={{ padding: SPACING.md, maxWidth: 720, margin: "0 auto" }}>
      <Title level="2" style={{ marginBottom: SPACING.sm }}>
        {t("scc_required_modal.title")}
      </Title>
      <Caption level="1" style={{ color: HINT_COLOR }}>
        {t("scc_required_modal.body")}
      </Caption>

      {sccQuery.isLoading && <p>{t("scc_required_modal.loading")}</p>}
      {sccQuery.isError && (
        <p style={{ color: DESTRUCTIVE_COLOR }} role="alert">
          {t("scc_required_modal.load_error", { error: String(sccQuery.error) })}
        </p>
      )}

      {sccQuery.data && (
        <>
          <p
            style={{
              fontSize: TYPOGRAPHY.label.size,
              marginTop: SPACING.md,
              fontWeight: 600,
            }}
          >
            {t("scc_required_modal.text_inline_label")}
          </p>
          {/* INLINE HTML viewer — NOT embedded PDF (ISSUE-07 fix).
              Card type="plain"; ref/onScroll/data-testid scroll-gate preserved. */}
          <Card type="plain" style={{ marginTop: SPACING.xs }}>
            <div
              ref={containerRef}
              data-testid="scc-markdown-scrollable"
              onScroll={onScroll}
              style={{
                padding: SPACING.md,
                maxHeight: "55vh",
                overflowY: "auto",
                fontSize: TYPOGRAPHY.body.size,
                lineHeight: 1.6,
              }}
            >
              {renderLegalMarkdown(sccQuery.data.markdown)}
            </div>
          </Card>

          {!scrolledToBottom && (
            <Caption
              level="1"
              style={{ color: HINT_COLOR, display: "block", marginTop: SPACING.xs }}
            >
              {t("scc_required_modal.scroll_to_bottom_hint")}
            </Caption>
          )}

          {/* PDF fallback (external browser via openLink — ISSUE-07) */}
          <Button
            size="s"
            mode="bezeled"
            onClick={openPdfFallback}
            data-testid="scc-download-pdf-fallback"
            style={{ marginTop: SPACING.sm }}
          >
            {t("scc_required_modal.download_pdf_fallback")}
          </Button>

          <Cell
            Component="label"
            multiline
            before={
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!scrolledToBottom}
                data-testid="scc-agree-checkbox"
                style={{ minWidth: 20, minHeight: 20 }}
              />
            }
          >
            {t("scc_required_modal.i_accept")}
          </Cell>

          {acceptMutation.isError && (
            <p style={{ color: DESTRUCTIVE_COLOR }} role="alert">
              {String(acceptMutation.error)}
            </p>
          )}

          <Button
            size="m"
            mode="filled"
            stretched
            disabled={!acceptEnabled}
            onClick={() => acceptMutation.mutate()}
            loading={acceptMutation.isPending}
            data-testid="scc-accept-button"
            style={{ marginTop: SPACING.md, minHeight: TOUCH_TARGET_MIN }}
          >
            {acceptMutation.isPending
              ? t("scc_required_modal.submitting")
              : t("scc_required_modal.i_accept")}
          </Button>
        </>
      )}
    </div>
  );
}
